const texts = {
  filterOptions: {
    ALL: 'All',
    CHECK_INS: 'Check-ins and Measurements',
    LEADS: 'Lead Activities',
    ONBOARDING: 'Onboarding Activities',
    PAYMENTS: 'Payment Updates',
    SUBSCRIPTION: 'Subscription',
    WORKOUTS: 'Workouts',
    HABITS: 'Habit Submissions',
    NUTRITION: 'Nutrition',
  },
  workoutComplete: 'Completed workout: “{workoutName}” assigned for {assignDate}',
  checkInSubmit: 'Submitted check-in for {weekText} {year}',
  failPayment: 'Payment of {currency}{amount} failed for the invoice “{invoice}”',
  paymentFailReason: '(Reason: {reason})',
  refundPayment: 'Refunded {currency}{amount} payment for the invoice “{invoice}”',
  successPayment: 'Payment of {currency}{amount} paid for the invoice “{invoice}”',
  habitCheckedOff: 'Completed habit(s): {habits} for the {weekText} {year}',
  send: 'Send',
  received: 'Received',
  message: '{msgType} a lead message: {message} ({time})',
  postPaymentForSubmit: 'Submitted post-payment form at {time}',
  signUp: 'Signed up at {time}',
  statusChange: '{type} status changed to {status}',
  callPlaced: 'Call placed by {userName}',
  lead: 'Lead',
  subscription: 'Subscription',
  filterLabel: 'Filter activity',
  emptyText: 'No "{filter}" history data to show for this client',
  empty: '',
  pausedDetails: 'Paused by: {userName}  •  Resumes at: {pauseEnd}',
  unpauseDetails: 'Unpaused by: {userName}',

  /*
    Copy for an event type with no `case` of its own. Never interpolated with a
    name, because the whole point is that it works for a type this file has
    never heard of.
  */
  unknownActivity: 'Activity recorded',

  // Workout assignments and check-in measurements.
  workoutAssigned: 'Assigned workout: “{workoutName}” for {assignDate}',
  /*
    A workout the client assigned to themselves. Worth its own line: "the coach
    programmed this" and "the client picked it" are different facts about a
    client's week, and the distinction is why `assignedBy` is on the event.
  */
  workoutSelfAssigned: 'Picked their own workout: “{workoutName}” for {assignDate}',
  workoutAssignedUnnamed: 'Assigned a workout for {assignDate}',
  workoutSelfAssignedUnnamed: 'Picked their own workout for {assignDate}',
  workoutAssignedExercises: '{exerciseCount} exercise(s): {exerciseNames}',
  programAssigned: 'Assigned program: “{programName}”',
  programAssignedUnnamed: 'Assigned a workout program',
  programAssignedWorkouts: '{workoutCount} workout(s)',
  measurementRecorded: 'Recorded measurements for {weekText} {year}',
  /*
    An edit to an already-submitted check-in appends a second event rather than
    rewriting the first, so both are in the log. Without distinct copy the pair
    reads as the client having measured themselves twice.
  */
  measurementRevised: 'Revised the measurements on the {weekText} {year} check-in',
  measurementRecordedUndated: 'Recorded measurements',
  measurementRevisedUndated: 'Revised previously recorded measurements',
  measurementLabels: {
    weight: 'Weight',
    stomach: 'Stomach',
    hip: 'Hip',
    upperArm: 'Upper arm',
    waist: 'Waist',
    chest: 'Chest',
    thigh: 'Thigh',
    glutes: 'Glutes',
  },
  unitLabels: {
    POUNDS: 'lbs',
    KILOGRAMS: 'kg',
    INCHES: 'in',
    CENTIMETERS: 'cm',
  },

  // Meal plans and recipe feedback.
  mealPlanAssigned: 'Assigned meal plan: “{planName}” ({assignedVia})',
  mealPlanAssignedUnnamed: 'Assigned a meal plan ({assignedVia})',
  mealPlanAssignedVia: {
    AI: 'generated by AI',
    AUTO: 'auto-assigned',
    COACH: 'assigned by coach',
  },
  mealPlanAssignedViaUnknown: 'assigned',
  mealPlanMealTimes: 'Meals: {mealTimes}',
  recipeLikedNamed: 'Liked the recipe “{recipeName}”',
  recipeDislikedNamed: 'Disliked the recipe “{recipeName}”',
  /*
    Kept for events written before the trigger started storing `recipeName`, and
    for the ones where the recipe had already been deleted when it ran. Vague
    rather than printing a recipe id at a coach.
  */
  recipeLiked: 'Liked a recipe',
  recipeDisliked: 'Disliked a recipe',
  recipeDislikedReasons: 'Reason(s): {reasons}',
  /*
    Switching a rating deletes one preference document and creates another, so a
    switch shows as a retraction followed by the new rating. The copy has to make
    the first of that pair read as a withdrawal.
  */
  recipeFeedbackRetractedNamed: 'Removed their {retracted} on “{recipeName}”',
  recipeFeedbackRetracted: 'Removed their {retracted} on a recipe',
  recipeFeedbackRetractedUnknown: 'Removed their rating on a recipe',
  recipePreferenceLabels: {
    LIKE: 'like',
    DISLIKE: 'dislike',
  },

  // Subscription lifecycle.
  /*
    Leads with the attribution, which is the whole reason this is recorded
    separately from `SUBSCRIPTION_STATUS_CHANGE`. On a Stripe-driven cancellation
    both land, and the status row says only "Subscription status changed to
    canceled" — so this line has to carry what that one cannot.
  */
  subscriptionCanceled: 'Subscription canceled',
  subscriptionCanceledBySource: 'Subscription canceled by {source}',
  subscriptionCanceledReason: 'Reason: {reason}',
  subscriptionCancelScheduled: 'Subscription set to cancel on {cancelDate}',
  // `RE_ONBOARD` is the internal name; a coach reads "reactivated".
  subscriptionReactivated: 'Reactivated their subscription',
  subscriptionChanged: 'Changed their subscription plan',
  subscriptionChangedToRecurring: 'Continuing with recurring billing',
  subscriptionChangedToMonthly: 'Continuing with a monthly commitment',
  /*
    An amendment is a different contract on new terms, not a change of billing
    cadence on the same one — so it names the plan rather than reusing
    `subscriptionChanged`, which would read identically to a plan change.
  */
  subscriptionAmended: 'Moved onto the {planName} plan',
  subscriptionAmendedUnnamed: 'Moved onto a new plan',
  subscriptionAmendedTerms: '{price}, from {startDate}',
  subscriptionAmendedTermsWithCommitment: '{price}, from {startDate} · {months}-month commitment',
  stripe: 'Stripe',

  // Coach exercise overrides.
  exerciseOverrideReplacedNamed: 'Substitution for “{originalName}” changed to “{newName}”',
  exerciseOverrideRemovedNamed: 'Removed the substitution for “{originalName}”',
  exerciseOverridePreviously: 'Previously “{prevName}”',
  /*
    Fallbacks for events written before the handler started resolving exercise
    names, and for exercises deleted before it ran. `actionedBy` is a user id
    and is never shown.
  */
  exerciseOverrideReplaced: 'Changed an exercise substitution',
  exerciseOverrideRemoved: 'Removed an exercise substitution',
};

export default texts;
